import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import { formatDateDDMMYYYY } from '../../utils/dateFormat';
import DatePicker from '../../components/DatePicker/DatePicker';
import PageLoadingArea from '../../components/PageLoader/PageLoadingArea';
import usePageLoading from '../../hooks/usePageLoading';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import '../../styles/FormPage.css';

const emptyItem = {
    description: '',
    nose: '',
    length: '',
    width: '',
    isSubtraction: false
};

const CreateMeasureSheet = () => {
    const { sheetId } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState(emptyItem);
    const [tableGroups, setTableGroups] = useState([]);
    const [currentItems, setCurrentItems] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [editingTableId, setEditingTableId] = useState(null);
    const [currentTableTitle, setCurrentTableTitle] = useState('');
    const [partyTitle, setPartyTitle] = useState('');
    const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const { isLoading, withLoading } = usePageLoading(Boolean(sheetId));

    useEffect(() => {
        if (sheetId) {
            withLoading(async () => {
                const response = await api.get(`/measuresheets/${sheetId}`);
                const data = response.data;
                setPartyTitle(data.partyName);

                const date = new Date(data.sheetDate);
                const adjustedDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
                    .toISOString()
                    .split('T')[0];
                setSelectedDate(adjustedDate);

                const transformedGroups = data.groups.map(group => ({
                    id: group.id,
                    title: group.title,
                    total: group.total,
                    items: group.items.map(item => ({
                        id: item.id,
                        description: item.description,
                        nose: item.nose,
                        length: item.length,
                        width: item.width,
                        sqft: item.sqft,
                        isSubtraction: item.isSubtraction === 1
                    }))
                }));
                setTableGroups(transformedGroups);
            }).catch(() => {
                alert('Failed to load measure sheet data');
            });
        }
    }, [sheetId, withLoading]);

    const calculateSqFt = (nose, length, width) => {
        return nose && length && width
            ? (parseFloat(nose) * parseFloat(length) * parseFloat(width)).toFixed(2)
            : '0.00';
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const openAddItemModal = () => {
        setFormData(emptyItem);
        setEditingId(null);
        setIsItemModalOpen(true);
    };

    const openEditItemModal = (item) => {
        setFormData({
            description: item.description || '',
            nose: item.nose ?? '',
            length: item.length ?? '',
            width: item.width ?? '',
            isSubtraction: !!item.isSubtraction
        });
        setEditingId(item.id);
        setIsItemModalOpen(true);
    };

    const closeItemModal = () => {
        setIsItemModalOpen(false);
        setEditingId(null);
        setFormData(emptyItem);
    };

    const handleSave = async () => {
        if (!partyTitle.trim()) {
            alert('Please enter a party name');
            return;
        }
        if (tableGroups.length === 0) {
            alert('Please save at least one measurement table');
            return;
        }

        const measureSheetData = {
            partyName: partyTitle,
            sheetDate: selectedDate,
            groups: tableGroups.map(group => ({
                title: group.title,
                total: group.total,
                items: group.items.map(item => ({
                    description: item.description,
                    nose: parseFloat(item.nose),
                    length: parseFloat(item.length),
                    width: parseFloat(item.width),
                    sqft: parseFloat(item.sqft),
                    isSubtraction: item.isSubtraction
                }))
            }))
        };

        try {
            if (sheetId) {
                await api.put(`/measuresheets/${sheetId}`, measureSheetData);
                alert('Measure sheet updated successfully');
            } else {
                await api.post('/measuresheets', measureSheetData);
                alert('Measure sheet saved successfully');
            }
        } catch (error) {
            console.error('Error saving measure sheet:', error);
            alert('Failed to save measure sheet');
        }
    };

    const handleSubmitItem = (e) => {
        e.preventDefault();
        if (!formData.nose || !formData.length || !formData.width) {
            alert('Please fill nose, length, and width');
            return;
        }

        const sqft = calculateSqFt(formData.nose, formData.length, formData.width);

        if (editingId !== null) {
            setCurrentItems(currentItems.map(item =>
                item.id === editingId ? { ...formData, sqft, id: editingId } : item
            ));
        } else {
            setCurrentItems([...currentItems, { ...formData, sqft, id: Date.now() }]);
        }

        closeItemModal();
    };

    const handleDelete = (id) => {
        if (!window.confirm('Remove this measurement?')) return;
        setCurrentItems(currentItems.filter(item => item.id !== id));
    };

    const handleEditTable = (group) => {
        setCurrentItems(group.items);
        setCurrentTableTitle(group.title);
        setEditingTableId(group.id);
        setTableGroups(prev => prev.filter(g => g.id !== group.id));
    };

    const calculateTableTotal = (items) => {
        return items.reduce((acc, item) => {
            return acc + (item.isSubtraction ? -parseFloat(item.sqft) : parseFloat(item.sqft));
        }, 0).toFixed(2);
    };

    const handleSaveTable = () => {
        if (currentItems.length === 0) {
            alert('Add at least one measurement before saving the table');
            return;
        }

        const newGroup = {
            id: editingTableId || Date.now(),
            items: currentItems,
            total: calculateTableTotal(currentItems),
            title: currentTableTitle || `Measure Sheet ${tableGroups.length + 1}`
        };
        setTableGroups([...tableGroups, newGroup]);
        setCurrentItems([]);
        setCurrentTableTitle('');
        setEditingTableId(null);
    };

    const handleDeleteTable = (groupId) => {
        if (!window.confirm('Delete this table?')) return;
        setTableGroups(tableGroups.filter(group => group.id !== groupId));
    };

    const generatePDF = () => {
        const doc = new jsPDF();
        let yOffset = 10;
        let grandTotal = 0;

        doc.setFontSize(8);
        doc.text('Measure Sheet', doc.internal.pageSize.width - 14, yOffset, { align: 'right' });
        yOffset += 5;

        doc.setFontSize(8);
        doc.text(`Date: ${formatDateDDMMYYYY(selectedDate)}`, doc.internal.pageSize.width - 14, yOffset, { align: 'right' });

        if (partyTitle) {
            doc.setFontSize(14);
            doc.text(partyTitle, doc.internal.pageSize.width / 2, yOffset - 3, { align: 'center' });

            yOffset += 5;
            doc.setLineWidth(0.5);
            doc.line(14, yOffset, doc.internal.pageSize.width - 14, yOffset);
            yOffset += 10;
        }

        tableGroups.forEach((group, index) => {
            doc.setFontSize(12);
            doc.text(`${index + 1}. ${group.title}`, 14, yOffset);
            yOffset += 10;

            autoTable(doc, {
                startY: yOffset,
                head: [['Description', 'Nose', 'Length', 'Width', 'SQ FT']],
                body: group.items.map(item => [
                    item.description,
                    item.nose,
                    item.length,
                    item.width,
                    item.sqft
                ]),
                styles: {
                    cellPadding: 2,
                    fontSize: 8,
                    overflow: 'linebreak',
                    valign: 'top'
                },
                headStyles: {
                    valign: 'middle'
                },
                columnStyles: {
                    0: { cellWidth: 90 },
                    1: { cellWidth: 22 },
                    2: { cellWidth: 22 },
                    3: { cellWidth: 22 },
                    4: { cellWidth: 24 }
                },
                didParseCell: function (data) {
                    if (data.row.index < group.items.length && group.items[data.row.index].isSubtraction) {
                        data.cell.styles.textColor = [255, 0, 0];
                    }
                }
            });

            doc.setFontSize(8);
            doc.text(`Total: ${group.total}`, 157, doc.lastAutoTable.finalY + 5);

            grandTotal += parseFloat(group.total);
            yOffset = doc.lastAutoTable.finalY + 10;
        });

        if (tableGroups.length > 0) {
            doc.setFontSize(12);
            doc.text(`Grand Total: ${grandTotal.toFixed(2)} SQ FT`, 14, yOffset);
        }

        doc.save('measure-sheet.pdf');
    };

    const grandTotal = tableGroups.reduce((acc, group) => acc + parseFloat(group.total), 0).toFixed(2);

    return (
        <>
        <PageLoadingArea isLoading={isLoading}>
            <div className="form-page">
                <div className="form-page-header">
                    <div>
                        <h1>{sheetId ? 'Edit measure sheet' : 'Add measure sheet'}</h1>
                        <p className="form-page-subtitle">
                            Record measurements by group, then save or export PDF
                        </p>
                    </div>
                    <div className="form-page-actions">
                        <button
                            type="button"
                            className="form-page-btn-secondary"
                            onClick={() => navigate('/measureSheet')}
                        >
                            Back to list
                        </button>
                    </div>
                </div>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>Sheet details</h2>
                            <p className="form-page-section-hint">Date and party for this measure sheet</p>
                        </div>
                    </div>
                    <div className="form-page-grid">
                        <div className="form-page-field">
                            <label htmlFor="sheetDate">Date</label>
                            <DatePicker
                                id="sheetDate"
                                name="sheetDate"
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="form-page-field">
                            <label htmlFor="partyTitle">Party name</label>
                            <input
                                type="text"
                                id="partyTitle"
                                value={partyTitle}
                                onChange={(e) => setPartyTitle(e.target.value)}
                                placeholder="Enter party name"
                            />
                        </div>
                    </div>
                </section>

                <section className="form-page-section">
                    <div className="form-page-section-head">
                        <div>
                            <h2>{editingTableId ? 'Editing group' : 'Current group'}</h2>
                            <p className="form-page-section-hint">
                                Add measurements, then save the group as a table
                            </p>
                        </div>
                        <button type="button" className="form-page-btn" onClick={openAddItemModal}>
                            Add item
                        </button>
                    </div>

                    <div className="form-page-grid" style={{ marginBottom: '1rem' }}>
                        <div className="form-page-field span-2">
                            <label htmlFor="groupTitle">Group title</label>
                            <input
                                type="text"
                                id="groupTitle"
                                value={currentTableTitle}
                                onChange={(e) => setCurrentTableTitle(e.target.value)}
                                placeholder="Enter title for this measurement group"
                            />
                        </div>
                    </div>

                    {currentItems.length === 0 ? (
                        <div className="form-page-empty">
                            <h3>No measurements yet</h3>
                            <p>Add items worked on to build this group.</p>
                            <button type="button" className="form-page-btn" onClick={openAddItemModal}>
                                Add first item
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="form-page-table-wrap">
                                <table className="form-page-table">
                                    <thead>
                                        <tr>
                                            <th className="col-desc">Description</th>
                                            <th className="col-dim">Nose</th>
                                            <th className="col-dim">Length</th>
                                            <th className="col-dim">Width</th>
                                            <th className="col-num">SQ FT</th>
                                            <th className="col-actions">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map(item => (
                                            <tr
                                                key={item.id}
                                                className={item.isSubtraction ? 'is-subtraction' : ''}
                                            >
                                                <td className="col-desc">{item.description}</td>
                                                <td className="col-dim">{item.nose}</td>
                                                <td className="col-dim">{item.length}</td>
                                                <td className="col-dim">{item.width}</td>
                                                <td className="col-num">{item.sqft}</td>
                                                <td className="col-actions">
                                                    <div className="row-actions">
                                                        <button
                                                            type="button"
                                                            className="form-page-btn-secondary small"
                                                            onClick={() => openEditItemModal(item)}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="form-page-btn-danger small"
                                                            onClick={() => handleDelete(item.id)}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                                <strong>Group total: {calculateTableTotal(currentItems)} SQ FT</strong>
                                <button type="button" className="form-page-btn" onClick={handleSaveTable}>
                                    Save group
                                </button>
                            </div>
                        </>
                    )}
                </section>

                {tableGroups.map((group, index) => (
                    <section key={group.id} className="form-page-section">
                        <div className="form-page-section-head">
                            <div>
                                <h2>{index + 1}. {group.title}</h2>
                                <p className="form-page-section-hint">Saved measurement group</p>
                            </div>
                            <div className="form-page-actions">
                                <button
                                    type="button"
                                    className="form-page-btn-secondary"
                                    onClick={() => handleEditTable(group)}
                                >
                                    Edit group
                                </button>
                                <button
                                    type="button"
                                    className="form-page-btn-danger"
                                    onClick={() => handleDeleteTable(group.id)}
                                >
                                    Delete group
                                </button>
                            </div>
                        </div>
                        <div className="form-page-table-wrap">
                            <table className="form-page-table">
                                <thead>
                                    <tr>
                                        <th className="col-desc">Description</th>
                                        <th className="col-dim">Nose</th>
                                        <th className="col-dim">Length</th>
                                        <th className="col-dim">Width</th>
                                        <th className="col-num">SQ FT</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {group.items.map(item => (
                                        <tr
                                            key={item.id}
                                            className={item.isSubtraction ? 'is-subtraction' : ''}
                                        >
                                            <td className="col-desc">{item.description}</td>
                                            <td className="col-dim">{item.nose}</td>
                                            <td className="col-dim">{item.length}</td>
                                            <td className="col-dim">{item.width}</td>
                                            <td className="col-num">{item.sqft}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ marginTop: '0.85rem', fontWeight: 650 }}>
                            Total: {group.total} SQ FT
                        </div>
                    </section>
                ))}

                {tableGroups.length > 0 && (
                    <section className="form-page-section">
                        <div className="form-page-summary">
                            <div className="form-page-summary-row total">
                                <span>Grand total</span>
                                <span>{grandTotal} SQ FT</span>
                            </div>
                        </div>
                    </section>
                )}

                <div className="document-page-footer-actions">
                    <button
                        type="button"
                        className="form-page-btn-secondary"
                        onClick={generatePDF}
                        disabled={tableGroups.length === 0}
                    >
                        Generate PDF
                    </button>
                    <button
                        type="button"
                        className="form-page-btn"
                        onClick={handleSave}
                    >
                        {sheetId ? 'Update sheet' : 'Save sheet'}
                    </button>
                </div>
            </div>
        </PageLoadingArea>

            {isItemModalOpen && (
                <div className="form-page-modal-overlay" onClick={closeItemModal}>
                    <div className="form-page-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="form-page-modal-head">
                            <div>
                                <h2>{editingId !== null ? 'Edit item' : 'Add item'}</h2>
                                <p>SQ FT is calculated from nose × length × width</p>
                            </div>
                            <button type="button" className="form-page-btn-ghost" onClick={closeItemModal}>
                                Close
                            </button>
                        </div>

                        <form id="measure-item-form" onSubmit={handleSubmitItem}>
                            <div className="form-page-grid">
                                <div className="form-page-field span-2">
                                    <label htmlFor="description">Description</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="3"
                                        autoFocus
                                        placeholder="Describe the work or material"
                                    />
                                </div>
                                <div className="form-page-field">
                                    <label htmlFor="nose">Nose</label>
                                    <input
                                        type="number"
                                        id="nose"
                                        name="nose"
                                        value={formData.nose}
                                        onChange={handleChange}
                                        required
                                        step="any"
                                    />
                                </div>
                                <div className="form-page-field">
                                    <label htmlFor="length">Length</label>
                                    <input
                                        type="number"
                                        id="length"
                                        name="length"
                                        value={formData.length}
                                        onChange={handleChange}
                                        required
                                        step="any"
                                    />
                                </div>
                                <div className="form-page-field">
                                    <label htmlFor="width">Width</label>
                                    <input
                                        type="number"
                                        id="width"
                                        name="width"
                                        value={formData.width}
                                        onChange={handleChange}
                                        required
                                        step="any"
                                    />
                                </div>
                                <div className="form-page-field">
                                    <label>Calculated SQ FT</label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={calculateSqFt(formData.nose, formData.length, formData.width)}
                                    />
                                </div>
                                <div className="form-page-field">
                                    <div className="form-page-checkbox">
                                        <input
                                            type="checkbox"
                                            id="isSubtraction"
                                            name="isSubtraction"
                                            checked={formData.isSubtraction}
                                            onChange={handleChange}
                                        />
                                        <label htmlFor="isSubtraction">Subtraction</label>
                                    </div>
                                </div>
                            </div>
                        </form>

                        <div className="form-page-modal-foot">
                            <button type="button" className="form-page-btn-secondary" onClick={closeItemModal}>
                                Cancel
                            </button>
                            <button type="submit" form="measure-item-form" className="form-page-btn">
                                {editingId !== null ? 'Update item' : 'Add item'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CreateMeasureSheet;
